import cloneDeep from 'lodash.clonedeep';
import { action, computed, makeObservable, observable } from 'mobx';
import { defaultWebhook, defaultWebhookParam, defaultWebhookValidation } from '../../../constants/webhooks';
import { getWebhookParams, webhookCast } from '../../../helpers/webhooks';
import { IResponseWebhook, IWebhook, IWebhookRequestParam, IWebhookRequestParamValidation, IWebhookValidation } from '../../../types/webhooks';
import client from 'src/services/client';
import { LoadingStatus } from 'src/types/main';
import { defaultValidation } from '~/constants/main';
import { getAbortController, isCanceledRequest } from '~/helpers';
import logger from '~/services/logger';
import { emptyStringValidation } from '~/utils/validation';

class WebhookEditStore {
  constructor() {
    makeObservable(this);
  }

  private getAbortController = getAbortController();
  private createAbortController = getAbortController();
  private updateAbortController = getAbortController();

  @observable public webhook: IWebhook = defaultWebhook;
  private backupWebhook: IWebhook = defaultWebhook;

  @observable public loading: LoadingStatus = LoadingStatus.Init;
  @observable public isEdit: boolean = false;

  @computed public get isLoading(): boolean {
    return this.loading === LoadingStatus.Loading;
  }

  @action public initStore = (id: string | undefined) => {
    this.validation = defaultWebhookValidation;
    this.webhook = defaultWebhook;
    this.backupWebhook = defaultWebhook;
    this.isEdit = false;

    if (id) this.getWebhook(id);
  };

  @action private getWebhook = async (id: string) => {
    this.getAbortController.abort();
    this.getAbortController = getAbortController();

    try {
      this.loading = LoadingStatus.Loading;

      const response: IResponseWebhook = await client.get(`v3/settings/webhooks/${id}`, {
        signal: this.getAbortController.signal,
      });

      const webhook: IWebhook = webhookCast(response);

      this.webhook = webhook;
      this.backupWebhook = webhook;
      this.loading = LoadingStatus.Success;
    } catch (error: any) {
      if (isCanceledRequest(error)) {
        this.loading = LoadingStatus.Success;
        return;
      }

      this.loading = LoadingStatus.Error;
      logger.error({ error, place: 'WebhookEditStore getWebhook', showModal: true });
    }
  };

  @action public changeWebhook = (value: Partial<IWebhook>) => {
    this.webhook = { ...this.webhook, ...value };
    this.isEdit = true;

    // update validation
    if (!this.validation.isValid) {
      this.setValidation();
    }
  };

  @action public addHeadersParam = () => {
    this.changeWebhook({ headers: this.webhook.headers.concat(defaultWebhookParam()) });
  };
  @action public deleteHeadersParam = (id: number) => {
    this.changeWebhook({ headers: this.webhook.headers.filter((webhook) => webhook.frontId !== id) });
  };
  @action public editHeadersParam = (newParam: IWebhookRequestParam) => {
    this.changeWebhook({ headers: this.webhook.headers.map((param) => (param.frontId === newParam.frontId ? newParam : param)) });
  };

  @action public addParametersParam = () => {
    this.changeWebhook({ parameters: this.webhook.parameters.concat(defaultWebhookParam()) });
  };
  @action public deleteParametersParam = (id: number) => {
    this.changeWebhook({ parameters: this.webhook.parameters.filter((webhook) => webhook.frontId !== id) });
  };

  @action public editParametersParam = (newParam: IWebhookRequestParam) => {
    this.changeWebhook({ parameters: this.webhook.parameters.map((param) => (param.frontId === newParam.frontId ? newParam : param)) });
  };

  @action public resetWebhook = () => {
    this.validation = defaultWebhookValidation;
    this.webhook = this.backupWebhook;
    this.isEdit = false;
  };

  @action public submit = () => {
    if (this.webhook.id) {
      return this.updateWebhook();
    } else {
      return this.createWebhook();
    }
  };

  private createWebhook = async () => {
    this.createAbortController.abort();
    this.createAbortController = getAbortController();

    try {
      this.loading = LoadingStatus.Loading;

      const params = getWebhookParams(this.webhook);

      await client.post(`v3/settings/webhooks`, params, {
        signal: this.createAbortController.signal,
      });

      this.isEdit = false;
      this.loading = LoadingStatus.Success;

      return true;
    } catch (error: any) {
      if (isCanceledRequest(error)) {
        this.loading = LoadingStatus.Success;
        return;
      }

      this.loading = LoadingStatus.Error;
      logger.error({ error, place: 'WebhookEditStore createWebhook', showModal: true });
    }
  };

  private updateWebhook = async () => {
    if (!this.webhook.id) return;

    this.updateAbortController.abort();
    this.updateAbortController = getAbortController();

    try {
      this.loading = LoadingStatus.Loading;

      const params = getWebhookParams(this.webhook);

      await client.put(`v3/settings/webhooks/${this.webhook.id}`, params, {
        signal: this.updateAbortController.signal,
      });

      this.isEdit = false;
      this.loading = LoadingStatus.Success;

      return true;
    } catch (error: any) {
      if (isCanceledRequest(error)) {
        this.loading = LoadingStatus.Success;
        return;
      }

      this.loading = LoadingStatus.Error;
      logger.error({ error, place: 'WebhookEditStore updateWebhook', showModal: true });
    }
  };

  private webhookRequestParamsValidation = (params: IWebhookRequestParam[]): IWebhookRequestParamValidation[] => {
    return params.map((param) => {
      const _param: IWebhookRequestParamValidation = {
        id: param.frontId,
        key: cloneDeep(defaultValidation),
        value: cloneDeep(defaultValidation),
      };

      // param key validation
      [_param.key.isValid, this.validation.isValid, _param.key.errorText] = emptyStringValidation(
        param.key,
        _param.key.isValid,
        this.validation.isValid
      );

      // param value validation
      [_param.value.isValid, this.validation.isValid, _param.value.errorText] = emptyStringValidation(
        param.value,
        _param.value.isValid,
        this.validation.isValid
      );

      return _param;
    });
  };

  @observable public validation: IWebhookValidation = cloneDeep(defaultWebhookValidation);
  @action public setValidation = () => {
    this.validation = cloneDeep(defaultWebhookValidation);

    // name validation
    [this.validation.name.isValid, this.validation.isValid, this.validation.name.errorText] = emptyStringValidation(
      this.webhook.name,
      this.validation.name.isValid,
      this.validation.isValid
    );

    // url validation
    [this.validation.url.isValid, this.validation.isValid, this.validation.url.errorText] = emptyStringValidation(
      this.webhook.url,
      this.validation.url.isValid,
      this.validation.isValid
    );

    // headers fields validation
    this.validation.headers = this.webhookRequestParamsValidation(this.webhook.headers);

    // parameters fields validation
    this.validation.parameters = this.webhookRequestParamsValidation(this.webhook.parameters);

    // statuses validation
    this.validation.statuses.isValid = Boolean(this.webhook.statuses.length);
    if (!this.validation.statuses.isValid) this.validation.isValid = false;

    return this.validation.isValid;
  };
}

export const webhookEditStore = new WebhookEditStore();
